import type { QueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { useAuthStore } from '@/lib/auth/store'

/**
 * The channel every tab of the app shares, one instance per tab.
 * BroadcastChannel is same-origin and never hands a message back to the
 * instance that posted it.
 */
const CHANNEL = 'mm-auth'

const signedOutMessageSchema = z.object({ type: z.literal('signed-out') })
const SIGNED_OUT: z.infer<typeof signedOutMessageSchema> = { type: 'signed-out' }

/**
 * Sign-out hygiene: one reaction, wired once at boot.
 *
 * Signing out has to do two things the store cannot do itself — empty the
 * React Query cache and send the member to /signin — and `lib/auth/store.ts`
 * can import neither the query client nor the router without inverting the
 * layering (the router's tree imports the store, through the guards). So the
 * store keeps its one job, announcing that the session ended, and this
 * subscription does the rest where both are in scope.
 *
 * Why a subscription and not a handler on the sign-out button: most sign-outs
 * are not clicks. The api client signs out whenever a refresh fails (see
 * `withSessionRenewal`), which happens outside React, on whatever screen the
 * member was reading, with no component involved. A button handler would leave
 * that path — the common one — clearing nothing. Hanging the reaction off the
 * store's own transition means every sign-out behaves identically, and there
 * is exactly one place that decides what a sign-out does.
 *
 * Emptying the cache is not tidiness. `me` and `onboarding` are cached with
 * `staleTime: Infinity`, so without this, member A signing out and member B
 * signing in on the same tab leaves B reading A's name, plan and persona until
 * a reload.
 *
 * `onSignedOut` is injected rather than imported because `@/app/router` pulls
 * in `src/routeTree.gen.ts`, which is generated and not committed — importing
 * it here would make this module, and its test, unloadable on a fresh clone.
 *
 * ── Other tabs ──
 *
 * A member who logs out means every tab, not the one they clicked in: the
 * refresh cookie is gone for all of them, but each would otherwise keep its
 * own access token, and its screen, for up to half an hour. So a member's
 * sign-out is announced on a BroadcastChannel, and a tab that hears it signs
 * out too — through the same store transition, so it clears and redirects
 * exactly as a click there would. It is not announced again from there, or
 * every tab would echo it to every other. Only `member`: an `expired` session
 * died at the cookie, which the other tabs share and will find out from on
 * their next refresh. A sign-in is not announced at all; a new tab signs
 * itself in from the cookie. Where the API is missing, tabs stay independent.
 *
 * Returns the teardown: the subscription and the channel.
 */
export const watchForSignOut = ({
  queryClient,
  onSignedOut,
}: {
  queryClient: QueryClient
  onSignedOut: () => void
}): (() => void) => {
  const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL)
  /** Set while this tab applies another tab's sign-out. */
  let fromAnotherTab = false

  if (channel) {
    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (!signedOutMessageSchema.safeParse(event.data).success) return
      // A tab still booting signs out too: its cookie refresh may have left
      // before the cookie was cleared, and the store drops that answer.
      if (useAuthStore.getState().status === 'anonymous') return
      fromAnotherTab = true
      try {
        useAuthStore.getState().signOut('member')
      } finally {
        fromAnotherTab = false
      }
    }
  }

  const unsubscribe = useAuthStore.subscribe((state, previous) => {
    // Only a real sign-out: authenticated → anonymous. A boot that finds no
    // session moves a signed-out visitor from 'unknown' to 'anonymous', which
    // is the same destination and means the opposite — treating it as a
    // sign-out would clear a cache nobody filled and throw someone reading
    // /welcome or /signup onto the sign-in screen.
    if (previous.status !== 'authenticated' || state.status !== 'anonymous') return

    if (state.signedOutBy === 'member' && !fromAnotherTab) channel?.postMessage(SIGNED_OUT)
    queryClient.clear()
    onSignedOut()
  })

  return () => {
    unsubscribe()
    channel?.close()
  }
}
